import React, { ChangeEvent, FC, useState, useEffect, useRef } from "react";
import QueryParams from "../../../interfaces/QueryParams";
import InputLabel from "../../../components/InputLabel";
import { SEARCH_LABELS } from "../../../utils/Enum";
import { multiSelectCountryRegion } from "../../../utils/MultiSelectCountryRegion";
import StyledInput from "../../../components/StyledInput/StyledInput";
import StyledButton from "../../../components/StyledButton/StyledButton";
import { CommodityApi } from "../../../api/CommodityApi";
import Select from "../../../components/Select/Select";
import { Option } from "../../../interfaces/Option";
import {
  validateNumericInput,
  validateStringInput,
} from "../../../utils/Validation";
import {
  NUMERIC_FIELD_LIMIT,
  VENDOR_NAME_FIELD_LIMIT,
  ITEM_DESCRIPTION_FIELD_LIMIT,
} from "@/constants";

const commonInputStyle = "pl-2 w-full min-w-[80px]";
const commonDropdownStyle = "w-full min-w-[80px] mb-[15px]";
const searchButtonStyle = "mt-6 w-[20vw] max-w-[150px]";

export interface SearchBarProps {
  searchParams: Partial<QueryParams>;
  updateSearchParams: (value: Partial<QueryParams>) => void;
  handleSearchClick: () => void;
}
const SearchBar: FC<SearchBarProps> = ({
  searchParams,
  updateSearchParams,
  handleSearchClick,
}) => {
  const [localSearchParams, setLocalSearchParams] =
    useState<Partial<QueryParams>>(searchParams);

  const [commodityList, setCommodityList] = useState<Option[]>([]);
  const [commoditySelect, setCommoditySelect] = useState<string[]>([]);
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false); // New loading state

  const doSearch = () => {
    const { country, vendorName, vendorNumber, itemNumber, itemDescription } =
      localSearchParams;
    const newSearchParams: Partial<QueryParams> = {
      country: country && country.length > 0 ? country : [],
      vendorName: vendorName?.trim() ?? undefined,
      vendorNumber: vendorNumber ? +vendorNumber : undefined,
      itemNumber: itemNumber ? +itemNumber : undefined,
      itemDescription: itemDescription?.trim() ?? undefined,
      commodity:
        commoditySelect.length > 0
          ? commoditySelect.map((e) => e.toLowerCase())
          : [],
    };
    updateSearchParams(newSearchParams);
    handleSearchClick();
  };
  /* Start:  state control functions */
  const handleSelectCountry = (selected: string[]) => {
    setLocalSearchParams({ ...localSearchParams, country: selected });
  };
  const handleVendorNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const alphaNumericValue = validateStringInput(
      event.target.value,
      VENDOR_NAME_FIELD_LIMIT,
    );
    setLocalSearchParams({
      ...localSearchParams,
      vendorName: alphaNumericValue || undefined,
    });
  };
  const handleVendorNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const numericValue = validateNumericInput(
      event.target.value,
      NUMERIC_FIELD_LIMIT,
    );
    setLocalSearchParams({
      ...localSearchParams,
      vendorNumber: numericValue ? parseInt(numericValue) : undefined,
    });
  };
  const handleItemNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const numericValue = validateNumericInput(
      event.target.value,
      NUMERIC_FIELD_LIMIT,
    );
    setLocalSearchParams({
      ...localSearchParams,
      itemNumber: numericValue ? parseInt(numericValue) : undefined,
    });
  };
  const handleItemDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const alphaNumericValue = validateStringInput(
      event.target.value,
      ITEM_DESCRIPTION_FIELD_LIMIT,
    );
    setLocalSearchParams({
      ...localSearchParams,
      itemDescription: alphaNumericValue ?? undefined,
    });
  };
  const handleCommodityChange = (selected: string[]) => {
    setCommoditySelect(selected);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // The key that was pressed
    const key = e.key;

    // A regex to check if the key is a digit
    const isDigit = /^\d$/.test(key);

    // A list of allowed control keys
    const isControlKey = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ].includes(key);

    // A check for copy/paste shortcuts
    const isShortcut =
      (e.ctrlKey || e.metaKey) && ["c", "v", "x", "a"].includes(key);

    // If the key is not a digit AND not a control key, block it
    if (!isDigit && !isControlKey && !isShortcut) {
      e.preventDefault();
    }
  };
  
  // Sync local commodity state with parent search parameters to prevent unnecessary re-renders
  useEffect(() => {
    const parentCommodity = searchParams.commodity ?? [];
    setCommoditySelect(prev => {
      if (
        prev.length === parentCommodity.length &&
        prev.every((v, i) => v === parentCommodity[i])
      ) {
        return prev; // no change, avoid re-render
      }
      return parentCommodity;
    });
  }, [searchParams.commodity]);

  useEffect(() => {
    const { itemDescription, itemNumber, vendorNumber, vendorName, country } =
      localSearchParams;
    const isFieldNotEmpty = (value: number | string | string[] | undefined) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value && value.toString().trim() !== "";
    };
    const searchAllowStatus = [
      itemDescription,
      itemNumber,
      vendorNumber,
      vendorName,
      country,
      commoditySelect,
    ].some(isFieldNotEmpty);
    setIsSearchButtonEnabled(searchAllowStatus);
  }, [commoditySelect, localSearchParams]);
  // End: state control functions

  // This ref is used to ensure that the commodity API is called only once
  const didCommodityGotCalled = useRef(false);

  useEffect(() => {
    const storedCommodityArrayList = sessionStorage.getItem(
      "commodityArrayListStored",
    );
    if (storedCommodityArrayList) {
      setCommodityList(JSON.parse(storedCommodityArrayList) as Option[]);
    }
    if (!didCommodityGotCalled.current) {
      setIsLoading(true); // Start loading
      CommodityApi.getCommodityList()
        .then((list: Option[]) => {
          setCommodityList(list || []);
        })
        .catch(() => {
          setCommodityList([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
      didCommodityGotCalled.current = true;
    }
  }, []);

  return (
    <div className="h-auto w-[90vw] pt-15 flex flex-col flex-wrap min-[1000px]:flex-row min-[800px]:flex-nowrap ">
      <div className={`${commonDropdownStyle} pr-4`}>
        <InputLabel
          id="input-label-searchbar-countryregion"
          label={SEARCH_LABELS.COUNTRYREGION}
          className="ml-2"
          htmlFor="country-region-select"
        />
        <Select
          id="country-region-select"
          dataTestID="searchbar-country-region-select"
          optionList={multiSelectCountryRegion}
          typeOfField={SEARCH_LABELS.COUNTRYREGION}
          onSelect={handleSelectCountry}
          valueOfField={localSearchParams.country || []}
        />
      </div>
      <StyledInput
        id="Vendor #"
        type="number"
        className={`${commonInputStyle} pr-4`}
        value={localSearchParams.vendorNumber}
        onKeyDown={handleKeyDown}
        onChange={handleVendorNumberChange}
      />
      <StyledInput
        id="Vendor Name"
        type="text"
        className={commonInputStyle}
        value={localSearchParams.vendorName}
        onChange={handleVendorNameChange}
      />
      <StyledInput
        id="Item Number"
        type="number"
        className={commonInputStyle}
        value={localSearchParams.itemNumber}
        onKeyDown={handleKeyDown}
        onChange={handleItemNumberChange}
      />
      <StyledInput
        id="Item Description"
        type="text"
        className={commonInputStyle}
        value={localSearchParams.itemDescription}
        onChange={handleItemDescriptionChange}
      />
      <div className={`pr-4 ${commonDropdownStyle}`}>
        <InputLabel
          id="input-label-searchbar-commodity"
          label={SEARCH_LABELS.COMMODITY}
          className="ml-2"
          htmlFor="commodity-select"
        />
        <Select
          id="commodity-select"
          dataTestID="searchbar-commodity-select"
          optionList={commodityList}
          typeOfField={SEARCH_LABELS.COMMODITY}
          onSelect={handleCommodityChange}
          valueOfField={commoditySelect}
          isLoading={isLoading}
        />
      </div>
      <div className="flex w-full justify-between">
        <StyledButton
          disabled={!isSearchButtonEnabled}
          id="searchbar-search-button"
          text={`Search`}
          onClick={doSearch}
          className={searchButtonStyle}
        />
      </div>
    </div>
  );
};
export default SearchBar;
